import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = [".open", "title", "subtitle"]
const _hoisted_2 = { class: "modal-content" }
const _hoisted_3 = { columns: "2" }
const _hoisted_4 = ["value", "disabled"]
const _hoisted_5 = ["columns"]
const _hoisted_6 = ["value"]
const _hoisted_7 = { key: 0 }
const _hoisted_8 = ["value"]
const _hoisted_9 = ["title"]
const _hoisted_10 = { slot: "footer" }
const _hoisted_11 = ["disabled"]

import { computed, ref, reactive, watch } from 'vue';
import { useStore } from 'vuex';

import { EPINIO_TYPES } from '../../types';
import { epinioExceptionToErrorsArray } from '../../utils/errors';
import { validateKubernetesName } from '@shell/utils/validators/kubernetes-name';
import { objValuesToString } from '../../utils/settings';
import Banner from '@components/Banner/Banner.vue';
import ChartValues from '../settings/ChartValues.vue';
import ResourceDropdown from '../application/ResourceDropdown.vue';
import { useNamespaces } from '../../utils/namespaces';

import isEqual from 'lodash/isEqual';


export default /*@__PURE__*/_defineComponent({
  __name: 'ServiceInstanceModal',
  setup(__props, { expose: __expose }) {

const store = useStore() as any;
const t = store.getters['i18n/t'];

// Modal open state
const showModal = ref(false);
const modalMode = ref<'create' | 'edit' | 'view'>('create');
// Model instance, used only for API calls
const serviceModel = ref<any>(null);

// Form fields (separate from the model to avoid proxy mutation issues)
const formNamespace = ref('');
const formName = ref('');
const formCatalogService = ref('');

const initialBoundApps = ref<string[]>([]);
const selectedApps = ref<string[]>([]);
const chartValues = reactive<Record<string, any>>({});
const validChartValues = ref<Record<string, boolean>>({});
const saving = ref(false);
const errors = ref<string[]>([]);

// Captured separately so background list polls (which omit internal_routes) can't wipe it
const internalRoutes = ref<string[]>([]);

const isLoadingCatalogServices = ref(false);
const cachedCatalogServices = ref<any[]>([]);
const fetchedCatalogServices = ref<any[]>([]);

const isLoadingApplications = ref(false);
const cachedApplications = ref<any[]>([]);
const fetchedApplications = ref<any[]>([]);

const {
  options:   namespaceOpts,
  isLoading: isLoadingNamespaces,
  firstName: firstNamespace,
  fetchAll:  fetchNamespaces,
  search:    searchNamespaces,
  seed:      seedNamespace,
} = useNamespaces(store, {
  scopeToActiveFilter: true
});

const catalogServiceOpts = computed(() =>
  fetchedCatalogServices.value.map((cs: any) => ({
    label: `${cs.name} (${cs.short_description})`,
    value: cs.name,
  }))
);

const nsAppOptions = computed(() => {
  if (!formNamespace.value) return [];

  return fetchedApplications.value
    .map((a: any) => ({ label: a.meta.name, value: a.meta.name }));
});

const selectedCatalogService = computed(() =>
  fetchedCatalogServices.value.find((cs: any) => cs.name === formCatalogService.value)
);

const showChartValues = computed(() =>
  Object.keys(selectedCatalogService.value?.settings || {}).length !== 0 || (isEdit.value && Object.keys(chartValues).length !== 0)
);

const isEdit = computed(() => modalMode.value === 'edit');
const isView = computed(() => modalMode.value === 'view');

const isDirty = computed(() => {
  if (isView.value) return false;

  if (isEdit.value) {
    if (!serviceModel.value) return false;

    const settingsChanged = !isEqual(
      objValuesToString(chartValues),
      objValuesToString(serviceModel.value.settings || {})
    );
    const appsChanged = !isEqual(
      [...selectedApps.value].sort(),
      [...initialBoundApps.value].sort()
    );

    return settingsChanged || appsChanged;
  }

  return !!(formName.value || formCatalogService.value || selectedApps.value.length || Object.keys(chartValues).length);
});

const showDiscardConfirm = ref(false);

const validationPassed = computed(() => {
  if (isEdit.value) {
    if (!serviceModel.value) return false;

    const newSettings = !isEqual(
      objValuesToString(chartValues),
      objValuesToString(serviceModel.value.settings || {})
    );
    const appBindingChanged = !isEqual(
      [...selectedApps.value].sort(),
      [...initialBoundApps.value].sort()
    );

    return newSettings || appBindingChanged;
  }

  if (!formCatalogService.value) return false;
  if (!formName.value) return false;
  if (!formNamespace.value) return false;
  if (showChartValues.value && !Object.values(validChartValues.value).every((v) => !!v)) return false;

  const nameErrors = validateKubernetesName(formName.value, '', store.getters, undefined, []);
  const nsErrors = validateKubernetesName(formNamespace.value, '', store.getters, undefined, []);

  return nameErrors.length === 0 && nsErrors.length === 0;
});

async function openCreate(prefilledCatalogService?: string) {
  errors.value = [];
  modalMode.value = 'create';

  serviceModel.value = null;
  formNamespace.value = '';
  formName.value = '';
  formCatalogService.value = prefilledCatalogService || '';

  selectedApps.value = [];
  initialBoundApps.value = [];
  Object.keys(chartValues).forEach(k => delete chartValues[k]);
  validChartValues.value = {};

  // Open first so the user sees the modal while the namespaces load, then
  // default to the first one as before.
  showModal.value = true;

  await fetchNamespaces();

  if (!formNamespace.value) {
    formNamespace.value = firstNamespace.value;
  }
}

function openView(row: any) {
  errors.value = [];
  modalMode.value = 'view';

  serviceModel.value = row;
  formNamespace.value = row.meta?.namespace || '';
  seedNamespace(formNamespace.value);
  formName.value = row.name || row.meta?.name || '';
  formCatalogService.value = row.catalog_service || '';
  internalRoutes.value = [];

  selectedApps.value = [...(row.boundapps || [])];
  initialBoundApps.value = [...(row.boundapps || [])];

  const settings = objValuesToString(row.settings || {});

  Object.keys(chartValues).forEach(k => delete chartValues[k]);
  Object.assign(chartValues, settings);
  validChartValues.value = {};

  showModal.value = true;

  // The list endpoint omits internal_routes, fetch the full record and capture routes locally
  // so background list polls (which also omit internal_routes) cannot wipe them.
  row.forceFetch().then(() => {
    if (!showModal.value) return;
    const id = `${ row.meta?.namespace }/${ row.meta?.name || row.name }`;
    const updated = store.getters['epinio/byId'](EPINIO_TYPES.SERVICE_INSTANCE, id);

    if (updated) {
      serviceModel.value = updated;
      internalRoutes.value = [...(updated.internal_routes || [])];
    }
  }).catch(() => {});
}

function openEdit(row: any) {
  errors.value = [];
  modalMode.value = 'edit';

  serviceModel.value = row;
  formNamespace.value = row.meta?.namespace || '';
  seedNamespace(formNamespace.value);
  formName.value = row.name || row.meta?.name || '';
  formCatalogService.value = row.catalog_service || '';
  internalRoutes.value = [];

  selectedApps.value = [...(row.boundapps || [])];
  initialBoundApps.value = [...(row.boundapps || [])];

  const settings = objValuesToString(row.settings || {});

  Object.keys(chartValues).forEach(k => delete chartValues[k]);
  Object.assign(chartValues, settings);
  validChartValues.value = {};

  showModal.value = true;

  // The list endpoint omits internal_routes, fetch the full record and capture routes locally
  // so background list polls (which also omit internal_routes) cannot wipe them.
  row.forceFetch().then(() => {
    if (!showModal.value) return;
    const id = `${ row.meta?.namespace }/${ row.meta?.name || row.name }`;
    const updated = store.getters['epinio/byId'](EPINIO_TYPES.SERVICE_INSTANCE, id);

    if (updated) {
      serviceModel.value = updated;
      internalRoutes.value = [...(updated.internal_routes || [])];
    }
  }).catch(() => {});
}

function handleModalClose() {
  if (isDirty.value) {
    showDiscardConfirm.value = true;
  } else {
    closeModal();
  }
}

function handleKeepEditing() {
  showDiscardConfirm.value = false;
}

function handleDiscard() {
  showDiscardConfirm.value = false;
  closeModal();
}

function closeModal() {
  // Clear form state before setting showModal = false so that when Lit fires
  // modal-close (which triggers handleModalClose), isDirty is already false
  formName.value = '';
  formCatalogService.value = '';
  formNamespace.value = '';
  selectedApps.value = [];
  initialBoundApps.value = [];
  Object.keys(chartValues).forEach(k => delete chartValues[k]);
  validChartValues.value = {};
  errors.value = [];
  internalRoutes.value = [];
  serviceModel.value = null;
  showDiscardConfirm.value = false;
  showModal.value = false;
}

function resetChartValues() {
  Object.keys(chartValues).forEach(k => delete chartValues[k]);
  validChartValues.value = {};
}

// Refresh the whole list, not just the saved record: the table paginates
// server-side, so a single-resource fetch adds an 11th row to a 10-row page
// and leaves the page count stale until the 30s poller catches up.
const refreshServices = () => store
  .dispatch('epinio/refreshList', { type: EPINIO_TYPES.SERVICE_INSTANCE })
  .catch(() => {});

// The Bound Applications column reads from the apps slice, so app bindings
// need that list refreshed too.
const refreshApps = () => store
  .dispatch('epinio/findAll', { type: EPINIO_TYPES.APP, opt: { force: true } })
  .catch(() => {});

async function onSubmit() {
  if (!validationPassed.value || saving.value) return;

  saving.value = true;
  errors.value = [];

  try {
    if (!isEdit.value) {
      const svc = await store.dispatch('epinio/create', { type: EPINIO_TYPES.SERVICE_INSTANCE });

      // Capture values before closeModal() wipes form state
      const capturedNamespace = formNamespace.value;
      const capturedName = formName.value;
      const capturedSelectedApps = [...selectedApps.value];

      // Create the service instance, then bind apps and refresh in the background
      svc.metadata = { namespace: capturedNamespace, name: capturedName };
      svc.catalog_service = formCatalogService.value;

      const cleanSettings = { ...chartValues };

      delete cleanSettings.value;
      svc.settings = Object.keys(cleanSettings).length ? objValuesToString(cleanSettings) : undefined;

      // Wait for the install only when apps are waiting to be bound to it
      await svc.create(capturedSelectedApps.length > 0);

      // Re-assert metadata: followLink merges the sparse create response back
      // into the model, which can wipe metadata and break subsequent bind calls
      svc.metadata = { namespace: capturedNamespace, name: capturedName };

      closeModal();

      store.dispatch('growl/success', {
        title:   t('epinio.growl.serviceInstance.create.success.title'),
        message: t('epinio.growl.serviceInstance.create.success.message', { name: capturedName }),
      });

      // Show the new item quickly, then bind apps and refresh again once done
      refreshServices();
      if (capturedSelectedApps.length) {
        Promise.all(capturedSelectedApps.map((app: string) => svc.bindApp(app)))
          .then(() => {
            refreshApps();
            refreshServices();
          })
          .catch(() => {});
      }
    } else {
      const svc = serviceModel.value;
      const newSettings = !isEqual(
        objValuesToString(chartValues),
        objValuesToString(svc.settings || {})
      );

      if (newSettings) {
        const cleanSettings = { ...chartValues };

        delete cleanSettings.value;
        svc.settings = objValuesToString(cleanSettings);
        await svc.update();
      }

      const bindApps = selectedApps.value;
      const unbindApps = initialBoundApps.value.filter(a => !bindApps.includes(a));
      const newBindApps = bindApps.filter(a => !initialBoundApps.value.includes(a));
      const serviceName = svc.meta?.name;

      closeModal();

      store.dispatch('growl/success', {
        title:   t('epinio.growl.serviceInstance.update.success.title'),
        message: t('epinio.growl.serviceInstance.update.success.message', { name: serviceName }),
      });

      // Bind/unbind and refresh in the background
      Promise.all([
        ...newBindApps.map((a: string) => svc.bindApp(a)),
        ...unbindApps.map((a: string) => svc.unbindApp(a)),
      ]).then(() => {
        refreshApps();
        refreshServices();
      }).catch(() => {});
      svc.forceFetch().catch(() => {});
    }
  } catch (err: any) {
    errors.value = epinioExceptionToErrorsArray(err);
    store.dispatch('growl/error', {
      title: isEdit.value
        ? t('epinio.growl.serviceInstance.save.error.updateTitle')
        : t('epinio.growl.serviceInstance.save.error.createTitle'),
      message: t('epinio.growl.serviceInstance.save.error.message'),
    });
  } finally {
    saving.value = false;
  }
}

const fetchCatalogServices = async () => {
  if (cachedCatalogServices.value.length > 0) {
    fetchedCatalogServices.value = cachedCatalogServices.value;
    return;
  }
  isLoadingCatalogServices.value = true;
  try {
    const res = await store.dispatch('epinio/request', {
      opt: {
        url: `/api/v1/catalogservices`,
        method: 'GET',
        responseType: 'json'
      }
    });
    const rawData = res.data ?? [];

    // classify raw JSON into proper model instances
    const classifiedData = await Promise.all(rawData.map((item: any) =>
      store.dispatch('epinio/create', { type: EPINIO_TYPES.CATALOG_SERVICE, ...item })
    ));
    fetchedCatalogServices.value = classifiedData;
    cachedCatalogServices.value = classifiedData;
  } catch (error) {
    console.error('Failed to fetch services', error);
  } finally {
    isLoadingCatalogServices.value = false;
  }
};

async function searchCatalogServices(query: string) {
  isLoadingCatalogServices.value = true;
  try {
    const res = await store.dispatch('epinio/request', {
      opt: {
        url: `/api/v1/catalogservices?search=${query}`,
        method: 'GET',
        responseType: 'json'
      }
    });
    const rawData = res.data ?? [];
    const classifiedData = await Promise.all(rawData.map((item: any) =>
      store.dispatch('epinio/create', { type: EPINIO_TYPES.CATALOG_SERVICE, ...item })
    ));
    fetchedCatalogServices.value = classifiedData;
  } catch {
    fetchedCatalogServices.value = [];
  } finally {
    isLoadingCatalogServices.value = false;
  }
}

async function fetchApplications() {
  if (!formNamespace.value) return;

  if (cachedApplications.value.length > 0) {
    fetchedApplications.value = cachedApplications.value;
    return;
  }

  isLoadingApplications.value = true;
  try {
    const res = await store.dispatch('epinio/request', {
      opt: {
        url: `/api/v1/applications?namespaces=${formNamespace.value}`,
        method: 'GET',
        responseType: 'json'
      }
    });
    const rawData = res.data ?? [];
    const classifiedData = await Promise.all(rawData.map((item: any) =>
      store.dispatch('epinio/create', { type: EPINIO_TYPES.APP, ...item })
    ));
    fetchedApplications.value = classifiedData;
    cachedApplications.value = classifiedData;
  } catch (error) {
    console.error('Failed to fetch applications', error);
  } finally {
    isLoadingApplications.value = false;
  }
}  

async function searchApplications(query: string) {
  if (!formNamespace.value) return;

  isLoadingApplications.value = true;
  try {
    const res = await store.dispatch('epinio/request', {
      opt: {
        url: `/api/v1/applications?namespaces=${formNamespace.value}&search=${query}`,
        method: 'GET',
        responseType: 'json'
      }
    });
    const rawData = res.data ?? [];
    const classifiedData = await Promise.all(rawData.map((item: any) =>
      store.dispatch('epinio/create', { type: EPINIO_TYPES.APP, ...item })
    ));
    fetchedApplications.value = classifiedData;
  } catch {
    fetchedApplications.value = [];
  } finally {
    isLoadingApplications.value = false;
  }
}

// watch namespace changes to fetch applications for the selected namespace
watch(formNamespace, (newNamespace) => {
  if (newNamespace) {
    fetchedApplications.value = [];
    cachedApplications.value = [];
    fetchApplications();
  }
}, { immediate: true });

__expose({ openCreate, openEdit, openView });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("trailhand-modal", {
    ".open": showModal.value,
    ".dismissible": false,
    title: (isEdit.value || isView.value) ? formName.value : 'Instances',
    subtitle: (isEdit.value || isView.value) ? (serviceModel.value?.stateDisplay || '') : 'Create New',
    onModalClose: handleModalClose
  }, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("trailhand-form-card", null, [
        _createElementVNode("trailhand-form-row", _hoisted_3, [
          _createVNode(ResourceDropdown, {
            value: formNamespace.value,
            options: _unref(namespaceOpts),
            label: "Namespace",
            placeholder: "Select a namespace",
            disabled: isEdit.value || isView.value,
            required: !isView.value,
            onDropdownChange: (e) => { formNamespace.value = e.detail.value; selectedApps.value = []; },
            fetchAllResources: _unref(fetchNamespaces),
            searchResources: _unref(searchNamespaces),
            isLoading: _unref(isLoadingNamespaces)
          }, null, 8, ["value", "options", "disabled", "required", "onDropdownChange", "fetchAllResources", "searchResources", "isLoading"]),
          _cache[4] || (_cache[4] = _createTextVNode()),
          _createElementVNode("trailhand-text-input", {
            value: formName.value,
            label: "Name",
            placeholder: "A Unique Name",
            required: true,
            disabled: isEdit.value || isView.value,
            onTextInputChange: _cache[0] || (_cache[0] = (e) => { formName.value = e.detail.value; })
          }, null, 40, _hoisted_4)
        ]),
        _cache[6] || (_cache[6] = _createTextVNode()),
        _createElementVNode("trailhand-form-row", {
          columns: (isView.value || isEdit.value) ? '4' : '1'
        }, [
          _createVNode(ResourceDropdown, {
            value: formCatalogService.value,
            options: catalogServiceOpts.value,
            label: 'Catalog Service',
            disabled: isEdit.value || isView.value,
            filterable: "",
            placeholder: "Select the type of service to create",
            onDropdownChange: (e) => { formCatalogService.value = e.detail.value; resetChartValues(); },
            fetchAllResources: fetchCatalogServices,
            searchResources: searchCatalogServices,
            isLoading: isLoadingCatalogServices.value
          }, null, 8, ["value", "options", "disabled", "onDropdownChange", "isLoading"]),
          _cache[5] || (_cache[5] = _createTextVNode()),
          (isView.value || isEdit.value)
            ? (_openBlock(), _createElementBlock("trailhand-text-input", {
                key: 0,
                value: serviceModel.value?.catalog_service_version || '',
                label: "Cat. Service Version",
                disabled: true
              }, null, 8, _hoisted_6))
            : _createCommentVNode("", true)
        ], 8, _hoisted_5),
        _cache[7] || (_cache[7] = _createTextVNode()),
        ((isView.value || isEdit.value) && internalRoutes.value.length)
          ? (_openBlock(), _createElementBlock("trailhand-form-row", _hoisted_7, [
              _createElementVNode("trailhand-code-editor", {
                style: {"width":"100%"},
                value: internalRoutes.value.join('\n'),
                label: "Internal Routes",
                disabled: true
              }, null, 8, _hoisted_8)
            ]))
          : _createCommentVNode("", true),
        _cache[8] || (_cache[8] = _createTextVNode()),
        _createElementVNode("trailhand-form-row", null, [
          _createVNode(ResourceDropdown, {
            values: selectedApps.value,
            options: nsAppOptions.value,
            label: "Bind to Application (Optional)",
            disabled: isView.value,
            filterable: "",
            multiselect: "",
            placeholder: "Select applications to bind",
            onDropdownChange: (e) => { selectedApps.value = e.detail.values; },
            fetchAllResources: fetchApplications,
            searchResources: searchApplications,
            isLoading: isLoadingApplications.value
          }, null, 8, ["values", "options", "disabled", "onDropdownChange", "isLoading"])
        ]),
        _cache[9] || (_cache[9] = _createTextVNode()),
        (showChartValues.value)
          ? (_openBlock(), _createElementBlock("trailhand-form-row", {
              key: 1,
              title: _unref(t)('epinio.services.chartValues.title')
            }, [
              _createVNode(ChartValues, {
                value: chartValues,
                "onUpdate:value": _cache[1] || (_cache[1] = ($event: any) => ((chartValues) = $event)),
                chart: selectedCatalogService.value.settings,
                title: _unref(t)('epinio.services.chartValues.title'),
                mode: isEdit.value ? 'edit' : 'create',
                disabled: isView.value,
                onValid: _cache[2] || (_cache[2] = ($event: any) => (validChartValues.value = $event))
              }, null, 8, ["value", "chart", "title", "mode", "disabled"])
            ], 8, _hoisted_9))
          : _createCommentVNode("", true)
      ]),
      _cache[10] || (_cache[10] = _createTextVNode()),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(errors.value, (err, i) => {
        return (_openBlock(), _createBlock(Banner, {
          key: i,
          color: "error",
          label: err
        }, null, 8, ["label"]))
      }), 128))
    ]),
    _cache[16] || (_cache[16] = _createTextVNode()),
    _createElementVNode("div", _hoisted_10, [
      (isView.value)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            _createElementVNode("trailhand-button", {
              variant: "secondary",
              class: "mr-10",
              onButtonClick: closeModal
            }, "\n          Close\n        ", 32),
            _cache[11] || (_cache[11] = _createTextVNode()),
            _createElementVNode("trailhand-button", {
              variant: "primary",
              onButtonClick: _cache[3] || (_cache[3] = ($event: any) => (modalMode.value = 'edit'))
            }, "\n          Edit Configuration\n        ", 32)
          ], 64))
        : (showDiscardConfirm.value)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
              _cache[12] || (_cache[12] = _createElementVNode("span", { class: "discard-message" }, "You have unsaved changes.", -1)),
              _cache[13] || (_cache[13] = _createTextVNode()),
              _createElementVNode("trailhand-button", {
                variant: "secondary",
                class: "mr-10",
                onButtonClick: handleKeepEditing
              }, "\n          Keep Editing\n        ", 32),
              _cache[14] || (_cache[14] = _createTextVNode()),
              _createElementVNode("trailhand-button", {
                variant: "destructive",
                onButtonClick: handleDiscard
              }, "\n          Discard\n        ", 32)
            ], 64))
          : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
              _createElementVNode("trailhand-button", {
                variant: "secondary",
                class: "mr-10",
                onButtonClick: handleModalClose
              }, "\n          Cancel\n        ", 32),
              _cache[15] || (_cache[15] = _createTextVNode()),
              _createElementVNode("trailhand-button", {
                variant: "primary",
                disabled: !validationPassed.value || saving.value,
                onButtonClick: onSubmit
              }, _toDisplayString(saving.value ? (isEdit.value ? 'Saving...' : 'Creating...') : (isEdit.value ? _unref(t)('generic.save') : _unref(t)('generic.create'))), 41, _hoisted_11)
            ], 64))
    ])
  ], 40, _hoisted_1))
}
}

})